import React, { useState, useEffect, useCallback } from 'react';

import { Refresh as RefreshIcon } from '@styled-icons/material-twotone/Refresh';
import { formatInTimeZone } from 'date-fns-tz';
import { api } from 'services/api';
import { toast } from 'shared/toast';

import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Text,
  Flex,
  IconButton,
  Icon,
  SimpleGrid,
  HStack,
  Select,
  Skeleton,
} from '@chakra-ui/react';

import AppTable, { createColumns } from 'components/AppTable';
import { ModalRootProps } from 'components/Modal/Root';
import PaginationWrapper from 'components/Pagination';

import useThrottledState from 'hooks/useThrottledState';

import {
  Input,
  InputSummary,
  State,
  StatePrice,
  DefaultPrice,
} from 'types/input';
import { Pagination, ServicePagination } from 'types/pagination';

type ElasticSort = {
  'sort[field]': string;
  'sort[mode]': 'asc' | 'desc';
};

interface ModalTrademarksAndDetailsLookupProps extends ModalRootProps {
  data: {
    inputId: number;
    inputDescription: string;
    searchFilter: Record<string, string | number>;
  };
}

const ModalTrademarksAndDetailsLookup: React.FC<
  ModalTrademarksAndDetailsLookupProps
> = ({ data: { inputId, inputDescription, searchFilter }, ...restProps }) => {
  const [data, setData] = useState<Input[]>([]);
  const [summary, setSummary] = useState<InputSummary>({} as InputSummary);
  const [loading, setLoading] = useState(false);

  const [pagination, setPagination] = useThrottledState<Pagination>(
    (() => {
      return {
        per_page: 20,
        page: 1,
      };
    })(),
    1000,
  );

  const [sort, setSort] = useState<string | null>('description');

  const processSort = useCallback((sortString: string | null): ElasticSort => {
    if (!sortString) return Object.create({});

    const isDesc = sortString.startsWith('-');
    const field = isDesc ? sortString.slice(1) : sortString;
    const mode = isDesc ? 'desc' : 'asc';

    return { 'sort[field]': field, 'sort[mode]': mode };
  }, []);

  const [servicePagination, setServicePagination] = useState<ServicePagination>(
    { last_page: 1 },
  );

  const getData = useCallback(async () => {
    setLoading(true);
    setData([]);

    try {
      const response = await api.get('/input', {
        params: {
          'filter[resource]': 'input',
          'filter[parent_id]': inputId,
          ...searchFilter,
          ...processSort(sort),
          page: pagination.page,
          per_page: pagination.per_page,
        },
      });

      const inputs = response.data;

      const newSummary = {
        total: inputs.meta ? inputs.meta.total : inputs.total,
      };

      const newPagination = {
        last_page: inputs.meta ? inputs.meta.last_page : inputs.last_page,
      };

      setData(inputs.data);
      setSummary(newSummary);
      setServicePagination(newPagination);
    } catch (err) {
      setData([]);
      setServicePagination({ last_page: 1 });

      toast({
        description:
          err.response?.data?.message ||
          'Houve um erro ao pesquisar os insumos. Tente novamente mais tarde!',
        status: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [pagination, searchFilter, sort, processSort, inputId]);

  useEffect(() => {
    getData();
  }, [getData]);

  useEffect(() => {
    if (pagination.page > servicePagination?.last_page) {
      setPagination((oldPagination) => {
        if (oldPagination.page > 1) {
          return {
            ...oldPagination,
            page: 1,
          };
        }

        return oldPagination;
      });
    }
  }, [pagination.page, servicePagination, setPagination]);

  function getCode(input: Input): string | null {
    const { trade_mark, detail } = input._source;

    if (trade_mark && detail) {
      return `${trade_mark.code}/${detail.code}`;
    }

    if (trade_mark) {
      return trade_mark.code;
    }

    if (detail) {
      return detail.code;
    }

    return null;
  }

  function isDefaultPrice(price: StatePrice): price is DefaultPrice {
    return 'default' in price;
  }

  const getStatePrice = (input: Input): State => {
    if (!input._source.prices) {
      return {
        default: {
          total: 0,
        },
      } as State;
    }

    const states = Array.from(Object.keys(input._source.prices));
    const state = states[0];

    return input._source.prices[state];
  };

  const getPrice = (input: Input): number => {
    const price = getStatePrice(input);

    if (isDefaultPrice(price)) {
      return price.default.total;
    }

    if (price.taxed) {
      return price.taxed.total;
    }

    if (price.untaxed) {
      return price.untaxed.total;
    }

    return 0;
  };

  const formatDatePrice = (date: string): string => {
    return formatInTimeZone(date, 'UTC', 'dd/MM/yyyy');
  };

  return (
    <Modal {...restProps} size="6xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Marcas e Detalhes</ModalHeader>

        <ModalCloseButton />

        <ModalBody pb={12}>
          <Flex direction="row" gap={1}>
            <Text fontWeight="bold">Insumo: </Text>
            <Text
              noOfLines={1}
              textOverflow="ellipsis"
              title={inputDescription}
            >
              {inputDescription || 'Não especificado'}
            </Text>
          </Flex>

          <Flex direction="row" gap={1} mb={8}>
            <Text fontWeight="bold">Total de preços: </Text>
            <Text noOfLines={1}>
              {loading ? (
                <Skeleton width={75} height={5} />
              ) : (
                summary.total || 0
              )}
            </Text>
          </Flex>

          <SimpleGrid columns={{ base: 1, md: 2 }} mt={3}>
            <HStack mb={3}>
              <Text fontSize="smaller">Por página:</Text>
              <Select
                width="auto"
                defaultValue={pagination.per_page}
                onChange={(e) => {
                  setPagination({
                    ...pagination,
                    per_page: Number(e.target.value),
                  });
                }}
              >
                {[5, 10, 20, 50].map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </Select>
            </HStack>

            <Flex
              flexWrap={{ base: 'wrap', md: 'nowrap' }}
              mb={3}
              gridGap={{ base: 0, md: 3 }}
              justifySelf="flex-end"
            >
              <IconButton
                size="sm"
                colorScheme="blue"
                title="Atualizar"
                aria-label="Atualizar"
                icon={<Icon as={RefreshIcon} w={5} h={5} />}
                isLoading={loading}
                onClick={getData}
                isDisabled={loading}
              />
            </Flex>
          </SimpleGrid>

          <AppTable
            cols={createColumns([
              { field: 'code', description: 'Código', sortable: false },
              { field: 'trade_mark', description: 'Marca', sortable: false },
              { field: 'detail', description: 'Detalhe', sortable: false },
              {
                field: 'prices',
                description: 'Valor Unit.',
                sortable: false,
                isNumeric: true,
              },
              {
                field: 'published_date',
                description: 'Data do preço',
                sortable: false,
                isCentered: true,
              },
              // { field: 'actions', description: 'Ações' },
            ])}
            mapping={(item) => ({
              id: item._id,
              code: getCode(item),
              trade_mark: (
                <Text
                  py={1}
                  maxWidth={{ base: '100%', md: '768px' }}
                  whiteSpace="pre-wrap"
                >
                  {item._source.trade_mark?.description || '-'}
                </Text>
              ),
              detail: (
                <Text
                  py={1}
                  maxWidth={{ base: '100%', md: '768px' }}
                  whiteSpace="pre-wrap"
                >
                  {item._source.detail?.description || '-'}
                </Text>
              ),
              prices: Intl.NumberFormat('pt-BR', {
                style: 'currency',
                currency: 'BRL',
              }).format(getPrice(item)),
              published_date: formatDatePrice(item._source.published_date),
              actions: null,
            })}
            data={data}
            loading={loading}
            onUpdateSort={(s) => setSort(s)}
          />

          <PaginationWrapper
            lastPage={servicePagination.last_page}
            onPaginate={(selectedPage) => {
              setPagination({ ...pagination, page: selectedPage });
            }}
          />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default ModalTrademarksAndDetailsLookup;
